/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn, SelectionMode, Text } from '@fluentui/react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { Table } from 'src/core/components/Table';
import { TitledSection } from 'src/core/components/TitledSection';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { globalMessages } from 'src/core/utils/globalMessages';

type Service = {
    serviceName: string;
    description: string;
    link: string;
};

const messages = defineMessages({
    title: {
        id: 'publishPane.servicesTable.nameColumnHeader',
        defaultMessage: 'Services available'
    },
    serviceColumnHeader: {
        id: 'publishPane.servicesTable.serviceColumnHeader',
        defaultMessage: 'Service'
    },
    descriptionColumnHeader: {
        id: 'publishPane.servicesTable.descriptionColumnHeader',
        defaultMessage: 'Description'
    },
    sentimentAnalysis: {
        id: 'publishPane.servicesTable.sentimentAnalysis',
        defaultMessage: 'Sentiment Analysis'
    },
    speechPriming: {
        id: 'publishPane.servicesTable.speechPriming',
        defaultMessage: 'Speech Priming'
    },
    sentimentAnalysisDescription: {
        id: 'publishPane.servicesTable.sentimentAnalysisDescription',
        defaultMessage: `Sentiment analysis evaluates text input and returns a sentiment score for each document, ranging from 0
(negative) to 1 (positive).`
    },
    speechPrimingDescription: {
        id: 'publishPane.servicesTable.speechPrimingDescription',
        defaultMessage: `Speech priming improves the recognition of spoken words and phrases that are commonly used in your bot.
For speech-enabled bots that use the Web Chat and Cortana channels, speech priming uses examples specified in Language
Understanding (LUIS) apps to improve speech recognition accuracy for important words.`
    },
    learnMoreDetailed: {
        id: 'publishPane.servicesTable.learnMoreDetailed',
        defaultMessage: 'Learn more about { serviceName }'
    }
});

/**
 * Displays a table of application versions.
 *
 * @param props The input properties for the table.
 */
export const ServicesTable = () => {
    const { localize } = useLocalization();

    const columns: IColumn[] = [
        {
            key: 'serviceName',
            name: localize(messages.serviceColumnHeader),
            fieldName: 'serviceName',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
            onRender: (item: Service) => <Text>{item.serviceName}</Text>
        },
        {
            key: 'description',
            name: localize(messages.descriptionColumnHeader),
            fieldName: 'description',
            minWidth: 150,
            isResizable: true,
            isMultiline: true,
            onRender: (item: Service) => (
                <LuisText>
                    {item.description}
                    &nbsp;
                    <ExternalLink aria-label={localize(messages.learnMoreDetailed, { serviceName: item.serviceName })} href={item.link}>
                        {localize(globalMessages.learnMore)}
                    </ExternalLink>
                </LuisText>
            )
        }
    ];

    const items: Service[] = [
        {
            serviceName: localize(messages.sentimentAnalysis),
            description: localize(messages.sentimentAnalysisDescription),
            link: 'https://docs.microsoft.com/azure/cognitive-services/luis/luis-quickstart-intent-and-sentiment-analysis'
        },
        {
            serviceName: localize(messages.speechPriming),
            description: localize(messages.speechPrimingDescription),
            link: 'https://docs.microsoft.com/azure/bot-service/bot-service-manage-speech-priming?view=azure-bot-service-3.0'
        }
    ];

    return (
        <TitledSection title="Services available">
            <Table selectionMode={SelectionMode.none} columns={columns} items={items} />
        </TitledSection>
    );
};
